import { type Easing } from 'framer-motion';
import { type ReactNode } from 'react';
import * as S from './styled';
import { buildOpacityKeyframes, buildScaleKeyframes } from './utils';

const RING_PHASES = [0.0242, 0.0473, 0.0703];

export type PulseCircleProps = {
  /** Ring svg element rendered inside each of the three animated rings */
  icon: ReactNode;
  /** Diameter (px) of a fully expanded ring */
  size: number;
  /** Duration (s) of the shared animation loop all rings repeat on */
  loopDuration: number;
  /** Absolute left offset (px) of the rings frame */
  left?: number;
  /** Absolute top offset (px) of the rings frame */
  top?: number;
  className?: string;
  /** Framer-motion transition for the rings' x/y position track; omit for a static ring */
  positionTransition?: { times: number[]; ease: Easing | Easing[] };
  /** x keyframes matching positionTransition.times */
  positionX?: number[];
  /** y keyframes matching positionTransition.times */
  positionY?: number[];
};

export const PulseCircle = ({
  icon,
  size,
  loopDuration,
  left = 0,
  top = 0,
  className,
  positionTransition,
  positionX,
  positionY,
}: PulseCircleProps) => (
  <S.RingsFrame className={className} $left={left} $size={size} $top={top}>
    {RING_PHASES.map((phase) => {
      const scale = buildScaleKeyframes(phase);
      const opacity = buildOpacityKeyframes(phase);
      return (
        <S.Ring
          key={phase}
          initial={{
            opacity: 0,
            scaleX: 0.01,
            scaleY: 0.01,
            ...(positionTransition ? { x: positionX?.[0] ?? 0, y: positionY?.[0] ?? 0 } : {}),
          }}
          animate={{
            opacity: opacity.values,
            scaleX: scale.values,
            scaleY: scale.values,
            ...(positionTransition ? { x: positionX, y: positionY } : {}),
          }}
          transition={{
            opacity: {
              duration: loopDuration,
              times: opacity.times,
              ease: opacity.ease,
              repeat: Infinity,
            },
            scaleX: {
              duration: loopDuration,
              times: scale.times,
              ease: scale.ease,
              repeat: Infinity,
            },
            scaleY: {
              duration: loopDuration,
              times: scale.times,
              ease: scale.ease,
              repeat: Infinity,
            },
            ...(positionTransition ? { x: positionTransition, y: positionTransition } : {}),
          }}
        >
          {icon}
        </S.Ring>
      );
    })}
  </S.RingsFrame>
);
